// Ortak onay diyaloğu — Geçmiş Rapor Aktarımı (Task 12, Tasarım review 12A).
// Geri çek / İptal et gibi geri dönüşü olan-ama-önemli aksiyonlarda `window.confirm`
// YERİNE kullanılır (12A — "No window.confirm anywhere in the import UI").
// Scaffolding TransferMenu.tsx'teki PreviewDialog'un (role=dialog + aria-modal + Esc +
// odak tuzağı) küçültülmüş ikizi — aynı desen, tek onay/vazgeç aksiyonu.
// DR12/1-2 (birim varyantı): `busy` iken Esc kapatmaz (dış tık zaten kapatmaz), onay düğmesi
// `busyLabel` + aria-busy; tuş dinleyicisi CAPTURE evresinde ve yayılmayı keser — iç içe
// açıldığında alttaki diyaloğun (KpiModal) Esc/Tab işleyicisine sızmaz; başka bir aria-modal
// diyalog açıksa onay dışındaki her şey `inert` (yalnız üstteki etkin), kapanınca geri alınır.

import { useEffect, useId, useRef, type ReactNode } from "react";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  /** gövde altında ek içerik (ör. bağlantı) — diyalog odak tuzağının içinde */
  extra?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** danger = kırmızı onay düğmesi (--color-error), neutral = mavi (--color-primary). */
  tone?: "danger" | "neutral";
  onConfirm: () => void;
  onCancel: () => void;
  /** İstek sürerken her iki düğme de disabled + onay düğmesi aria-busy; Esc kapatmaz. */
  busy?: boolean;
  /** busy iken onay düğmesi metni (ör. "Çevriliyor…"); verilmezse "…". */
  busyLabel?: string;
}

interface KeyLike {
  key: string;
  shiftKey: boolean;
  preventDefault: () => void;
  stopPropagation: () => void;
}

/** Diyaloğun belge tuş işleyicisi (capture) — Esc vazgeç (busy değilse), Tab odak tuzağı. */
export function handleConfirmDialogKey(
  e: KeyLike,
  ctx: { busy: boolean; onCancel: () => void; dialog: HTMLElement | null },
): void {
  if (e.key === "Escape") {
    e.preventDefault();
    e.stopPropagation();
    if (!ctx.busy) ctx.onCancel();
    return;
  }
  if (e.key !== "Tab" || !ctx.dialog) return;
  e.stopPropagation();
  const focusables = ctx.dialog.querySelectorAll<HTMLElement>(
    'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])',
  );
  if (focusables.length === 0) {
    e.preventDefault();
    return;
  }
  const first = focusables[0]!;
  const last = focusables[focusables.length - 1]!;
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

/**
 * `el`'in ata zinciri DIŞINDAKİ tüm kardeşleri `inert` yapar (iç içe diyalog: alttaki modalın
 * alanları + sayfa); önceden inert olanlara dokunmaz. Dönüş: geri alma fonksiyonu.
 */
export function inertOutside(el: Element): () => void {
  const touched: Element[] = [];
  let node: Element | null = el;
  while (node?.parentElement) {
    const parent: Element = node.parentElement;
    for (const sib of Array.from(parent.children)) {
      if (sib === node || sib.hasAttribute("inert")) continue;
      sib.setAttribute("inert", "");
      touched.push(sib);
    }
    node = parent;
  }
  return () => {
    for (const sib of touched) sib.removeAttribute("inert");
  };
}

export function ConfirmDialog(props: ConfirmDialogProps): JSX.Element | null {
  const {
    open,
    title,
    body,
    extra,
    confirmLabel,
    cancelLabel = "Vazgeç",
    tone = "neutral",
    onConfirm,
    onCancel,
    busy = false,
    busyLabel,
  } = props;
  const titleId = useId();
  const bodyId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  // busy/onCancel her render'da değişebilir; dinleyici yalnız `open`'a bağlı kalsın (odak
  // her üst render'da Vazgeç'e geri atlamasın) — son değer ref'ten okunur.
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  // Kapanınca odak diyaloğu açan öğeye döner (yoksa <body>'de kalırdı — UX P2 M2).
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      if (prev && document.contains(prev)) prev.focus();
    };
  }, [open]);

  // Açılışta odak Vazgeç'e (yanlışlıkla onaylamayı zorlaştırır); Esc kapatır (busy değilse);
  // Tab odak tuzağı diyalog içinde kalır. Capture: alttaki diyaloğun dinleyicisinden önce.
  useEffect(() => {
    if (!open) return;
    cancelRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      handleConfirmDialogKey(e, {
        busy: busyRef.current,
        onCancel: () => onCancelRef.current(),
        dialog: dialogRef.current,
      });
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open]);

  // DR12/2 — iç içe diyalog: başka bir aria-modal açıksa yalnız bu diyalog etkin.
  useEffect(() => {
    if (!open || !overlayRef.current) return;
    const others = Array.from(document.querySelectorAll('[aria-modal="true"]')).filter(
      (d) => d !== dialogRef.current,
    );
    if (others.length === 0) return;
    return inertOutside(overlayRef.current);
  }, [open]);

  if (!open) return null;

  const confirmColor = tone === "danger" ? "var(--color-error)" : "var(--color-primary)";

  return (
    <div
      ref={overlayRef}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        className="rounded-xl p-6 max-w-md w-full mx-4"
        style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
      >
        <h2
          id={titleId}
          style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem", marginBottom: 8 }}
        >
          {title}
        </h2>
        <p
          id={bodyId}
          style={{ color: "var(--color-text-2)", fontSize: "1rem", marginBottom: extra ? 8 : 20 }}
        >
          {body}
        </p>
        {extra ? <div style={{ marginBottom: 20 }}>{extra}</div> : null}
        <div className="flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg px-4 py-2 text-sm disabled:opacity-50"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-2)",
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            aria-busy={busy || undefined}
            className="rounded-lg px-4 py-2 text-sm disabled:opacity-50"
            style={{ background: confirmColor, color: "var(--color-surface)", fontWeight: 600 }}
          >
            {busy ? (busyLabel ?? "…") : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
